import { useEffect, useRef } from "react";

// Button dimensions for different game statuses
const BUTTON_CONFIG = {
  unreleased: {
    width: 139,
    height: 32,
    top: -148,
    left: -491,
  },
  released: {
    width: 113,
    height: 32,
    top: -153,
    left: -521,
  },
} as const;

type GameStatus = keyof typeof BUTTON_CONFIG;

interface SteamWishlistButtonProps {
  appId: string;
  gameStatus?: GameStatus;
  scale?: number;
  utmSource?: string;
  utmCampaign?: string;
  className?: string;
  buttonClassName?: string;
  label?: string;
  onClick?: () => void;
}

const SteamWishlistButton = ({
  appId,
  gameStatus = "unreleased",
  scale = 1,
  utmSource,
  utmCampaign,
  className,
  buttonClassName,
  label,
  onClick,
}: SteamWishlistButtonProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleBlur = () => {
      if (document.activeElement === iframeRef.current) {
        onClick?.();
      }
    };

    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("blur", handleBlur);
    };
  }, [onClick]);

  const defaultText =
    gameStatus === "released" ? "Buy on Steam" : "Add to Wishlist";
  const buttonText = label || defaultText;
  // Build the widget URL with optional UTM parameters
  let widgetUrl = `https://store.steampowered.com/widget/${appId}/`;

  const utmParams = new URLSearchParams();
  if (utmSource) utmParams.set("utm_source", utmSource);
  if (utmCampaign) utmParams.set("utm_campaign", utmCampaign);

  const utmString = utmParams.toString();
  if (utmString) {
    widgetUrl += `?${utmString}`;
  }

  const iframeWidth = 646;
  const iframeHeight = 190;

  const iframe = (
    <iframe
      ref={iframeRef}
      src={widgetUrl}
      width={iframeWidth}
      height={iframeHeight}
      style={{
        border: "none",
        display: "block",
        minWidth: iframeWidth,
      }}
      title={`Steam Widget for App ${appId}`}
      loading="lazy"
      allow="encrypted-media"
    />
  );

  const config = BUTTON_CONFIG[gameStatus];
  const scaledWidth = config.width * scale;
  const scaledHeight = config.height * scale;

  return (
    <div
      className={className}
      style={{
        position: "relative",
        display: "inline-block",
        width: scaledWidth,
        height: scaledHeight,
      }}
    >
      {/* Custom button on top - visible but clicks pass through */}
      <button
        type="button"
        className={buttonClassName}
        style={{
          pointerEvents: "none",
          width: "100%",
          height: "100%",
          backgroundColor: !buttonClassName ? "#4CAF50" : undefined, // Default only if no class
          color: !buttonClassName ? "white" : undefined,
          border: !buttonClassName ? "none" : undefined,
          borderRadius: !buttonClassName ? "4px" : undefined,
          fontSize: !buttonClassName ? "14px" : undefined,
          fontWeight: !buttonClassName ? "500" : undefined,
          cursor: "pointer",
          // Add styles to match the design if needed, but user provided this code
        }}
      >
        {buttonText}
      </button>
      {/* Hidden iframe button underneath - receives clicks */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: config.width,
          height: config.height,
          overflow: "hidden",
          opacity: 0.01,
          transform: scale !== 1 ? `scale(${scale})` : undefined,
          transformOrigin: "top left",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: config.top,
            left: config.left,
          }}
        >
          {iframe}
        </div>
      </div>
    </div>
  );
};

export { SteamWishlistButton, BUTTON_CONFIG };
export type { GameStatus };
